import generatedCatalog from '../../../generated/capability-catalog.v1.json'

export type OAuthScope = 'fusegrid:read' | 'fusegrid:write' | 'fusegrid:excel'
export type RegistrationKind = 'dcr' | 'cimd' | 'loopback_dcr'
export type CompanyRole = 'viewer' | 'client_editor' | 'consultant_owner' | 'site_admin'
export type CompanyMode = 'all_accessible' | 'selected'

export type ConsentCompany = {
  workbook_id: string
  company_key: string
  display_name: string
  role: CompanyRole | string
}

export type ConsentFirm = {
  firm_site_id: string
  firm_name: string
  companies: ConsentCompany[]
}

export type ConsentClient = {
  client_id: string
  registration_kind: RegistrationKind | string
  logo_uri: string | null
  verified: boolean
  label: string | null
}

export type OAuthConsentDetails = {
  request_id: string
  client_name: string
  client: ConsentClient
  user_email: string
  requested_scopes: string[]
  /** OpenID scopes (`openid`, `email`) granted with the connection; they carry no FuseGrid data. */
  identity_scopes: string[]
  /** The app receives the user's email address (the `email` scope). */
  shares_email: boolean
  allowed_scopes: string[]
  default_scopes: string[]
  company_count: number
  firms: ConsentFirm[]
}

export type ConsentDecisionBody =
  | { approved: false }
  | { approved: true; scopes: string[]; company_mode: 'all_accessible' }
  | { approved: true; scopes: string[]; company_mode: 'selected'; workbook_ids: string[] }

export const REQUIRED_SCOPE: OAuthScope = 'fusegrid:read'

const SCOPE_ORDER: string[] = ['fusegrid:read', 'fusegrid:write', 'fusegrid:excel']

const SCOPE_LABELS: Record<string, string> = {
  'fusegrid:read': 'Read financials and reports',
  'fusegrid:write': 'Make changes (goals, plans, workbook edits)',
  'fusegrid:excel': 'Act in your open Excel workbook',
}

export function scopeLabel(scope: string): string {
  return SCOPE_LABELS[scope] ?? scope
}

function sortScopes(scopes: Iterable<string>): string[] {
  const unique = Array.from(new Set(scopes))
  const rank = (scope: string) => {
    const index = SCOPE_ORDER.indexOf(scope)
    return index === -1 ? SCOPE_ORDER.length : index
  }
  return unique.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
}

/** Scopes the user can toggle, in display order. Read is always offered. */
export function offeredScopes(details: Pick<OAuthConsentDetails, 'allowed_scopes'>): string[] {
  return sortScopes([REQUIRED_SCOPE, ...details.allowed_scopes])
}

/** Initial checked scopes: defaults that are allowed, plus read. */
export function initialScopes(details: Pick<OAuthConsentDetails, 'allowed_scopes' | 'default_scopes'>): string[] {
  const allowed = new Set(offeredScopes(details))
  return sortScopes([REQUIRED_SCOPE, ...details.default_scopes.filter((scope) => allowed.has(scope))])
}

/** Requested scopes the user's role does not permit. */
export function deniedRequestedScopes(details: Pick<OAuthConsentDetails, 'allowed_scopes' | 'requested_scopes'>): string[] {
  const allowed = new Set(offeredScopes(details))
  return sortScopes(details.requested_scopes.filter((scope) => !allowed.has(scope)))
}

export function toggleScope(current: string[], scope: string, checked: boolean): string[] {
  if (scope === REQUIRED_SCOPE) return sortScopes([REQUIRED_SCOPE, ...current])
  const next = checked ? [...current, scope] : current.filter((value) => value !== scope)
  return sortScopes([REQUIRED_SCOPE, ...next])
}

export function isLoopbackClient(client: Pick<ConsentClient, 'registration_kind'>): boolean {
  return client.registration_kind === 'loopback_dcr'
}

/** Warning text for unverified clients, or null when the client is verified. */
export function clientWarning(client: ConsentClient): string | null {
  if (client.verified) return null
  if (client.label && client.label.trim()) return client.label.trim()
  return isLoopbackClient(client) ? 'Local app on this computer (unverified)' : 'Unverified app'
}

export type FirmGroup = {
  firm_site_id: string
  firm_name: string
  companies: ConsentCompany[]
}

/** Firms sorted by name, companies sorted by display name; empty firms dropped. */
export function groupCompaniesByFirm(firms: ConsentFirm[]): FirmGroup[] {
  const byFirm = new Map<string, FirmGroup>()
  for (const firm of firms) {
    const group = byFirm.get(firm.firm_site_id) ?? { firm_site_id: firm.firm_site_id, firm_name: firm.firm_name, companies: [] }
    const seen = new Set(group.companies.map((company) => company.workbook_id))
    for (const company of firm.companies) {
      if (seen.has(company.workbook_id)) continue
      seen.add(company.workbook_id)
      group.companies.push(company)
    }
    byFirm.set(firm.firm_site_id, group)
  }
  return Array.from(byFirm.values())
    .filter((group) => group.companies.length > 0)
    .map((group) => ({ ...group, companies: [...group.companies].sort((a, b) => a.display_name.localeCompare(b.display_name)) }))
    .sort((a, b) => a.firm_name.localeCompare(b.firm_name))
}

/** Case-insensitive filter over display_name, company_key and firm_name. */
export function filterFirmGroups(groups: FirmGroup[], query: string): FirmGroup[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return groups
  return groups
    .map((group) => {
      if (group.firm_name.toLowerCase().includes(needle)) return group
      const companies = group.companies.filter((company) =>
        company.display_name.toLowerCase().includes(needle) || company.company_key.toLowerCase().includes(needle))
      return { ...group, companies }
    })
    .filter((group) => group.companies.length > 0)
}

export function totalCompanies(firms: ConsentFirm[]): number {
  return groupCompaniesByFirm(firms).reduce((sum, group) => sum + group.companies.length, 0)
}

export type FirmSelectionState = 'none' | 'some' | 'all'

export function firmSelectionState(group: FirmGroup, selected: ReadonlySet<string>): FirmSelectionState {
  const count = group.companies.filter((company) => selected.has(company.workbook_id)).length
  if (count === 0) return 'none'
  return count === group.companies.length ? 'all' : 'some'
}

/** Select or clear every company in the firm group (only the companies passed in). */
export function setFirmSelection(selected: ReadonlySet<string>, group: FirmGroup, checked: boolean): Set<string> {
  const next = new Set(selected)
  for (const company of group.companies) {
    if (checked) next.add(company.workbook_id)
    else next.delete(company.workbook_id)
  }
  return next
}

export function canApprove(mode: CompanyMode, selected: ReadonlySet<string>, companyCount: number): boolean {
  if (companyCount <= 0) return false
  return mode === 'all_accessible' || selected.size > 0
}

export function buildApproveBody(scopes: string[], mode: CompanyMode, selectedWorkbookIds: Iterable<string>): ConsentDecisionBody {
  const finalScopes = sortScopes([REQUIRED_SCOPE, ...scopes])
  if (mode === 'all_accessible') return { approved: true, scopes: finalScopes, company_mode: 'all_accessible' }
  return { approved: true, scopes: finalScopes, company_mode: 'selected', workbook_ids: Array.from(new Set(selectedWorkbookIds)) }
}

export function buildDenyBody(): ConsentDecisionBody {
  return { approved: false }
}

const ERROR_MESSAGES: Record<string, string> = {
  authorization_request_expired: 'This connection request has expired. Start the connection again from your AI app.',
  invalid_scope: 'One of the selected permissions is not available for your role. Adjust the permissions and try again.',
  no_companies_selected: 'Choose at least one company, or allow all companies you can access.',
  company_not_accessible: 'One of the selected companies is no longer available to you. Refresh and choose again.',
}

export function consentErrorMessage(code: unknown, fallback: string): string {
  const key = typeof code === 'string' ? code : ''
  return ERROR_MESSAGES[key] ?? (key || fallback)
}

export type CatalogEntry = { name: string; title: string; scope: string }
export type CapabilityCatalog = { entries: CatalogEntry[] }

export type CapabilityScopeSummary = { scope: string; label: string; count: number; titles: string[] }

/** The generated catalog (C2): the same entries MCP and the sidebar dispatch. */
export const consentCatalog: CapabilityCatalog = generatedCatalog as CapabilityCatalog

/** Group catalog entries by scope, limited to the chosen scopes, in scope display order. */
export function summarizeCapabilities(scopes: string[], catalog: CapabilityCatalog = consentCatalog): CapabilityScopeSummary[] {
  const chosen = new Set(scopes)
  const byScope = new Map<string, string[]>()
  for (const entry of catalog.entries) {
    if (!chosen.has(entry.scope)) continue
    const titles = byScope.get(entry.scope) ?? []
    titles.push(entry.title)
    byScope.set(entry.scope, titles)
  }
  return sortScopes(byScope.keys()).map((scope) => {
    const titles = byScope.get(scope) ?? []
    return { scope, label: scopeLabel(scope), count: titles.length, titles }
  })
}
